import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { NEEDS } from "@/data/content";
import { SectionLabel } from "@/components/wf/primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", organisation: "", need: NEEDS[0], location: "", dates: "", details: "" };

const inputCls = "w-full bg-transparent border border-brand-teal/30 px-4 py-3 font-sans text-brand-sand placeholder:text-brand-sand/40 focus:border-brand-aqua focus:outline-none transition-colors";

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.need) {
      toast.error("Please add your name, email and what you need.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. We'll chart a course and reply shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 border-t border-brand-teal/15" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionLabel testid="contact-label">Plan with us</SectionLabel>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-sand leading-[1.03]">
            Tell us the goal. We'll set the course.
          </h2>
          <p className="mt-6 font-sans text-brand-sand/70 leading-relaxed max-w-md">
            Every enquiry gets a tailored quote — no fixed price lists, because no two voyages are the same.
            Share a little detail and we'll come back with a plan.
          </p>
          <div className="mt-10 space-y-2 font-sans text-sm text-brand-sand/60">
            <p><span className="text-brand-teal">Based in</span> · Funafuti, Tuvalu</p>
            <p><span className="text-brand-teal">Email</span> · tuvalulogistics@gmail.com</p>
            <p><span className="text-brand-teal">Phone</span> · +688 7102399</p>
          </div>
        </div>

        <motion.form onSubmit={submit} className="lg:col-span-7 grid sm:grid-cols-2 gap-4" data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <input className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} data-testid="contact-name" />
          <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="contact-email" />
          <input className={`${inputCls} sm:col-span-2`} placeholder="Organisation (optional)" value={form.organisation} onChange={set("organisation")} data-testid="contact-organisation" />
          <select className={`${inputCls} sm:col-span-2 appearance-none`} value={form.need} onChange={set("need")} data-testid="contact-need">
            {NEEDS.map((n) => <option key={n} value={n} className="bg-brand-navy text-brand-sand">{n}</option>)}
          </select>
          <input className={inputCls} placeholder="Island / location" value={form.location} onChange={set("location")} data-testid="contact-location" />
          <input className={inputCls} placeholder="Rough dates" value={form.dates} onChange={set("dates")} data-testid="contact-dates" />
          <textarea className={`${inputCls} sm:col-span-2 min-h-[130px] resize-none`} placeholder="Details — tell us what you're planning" value={form.details} onChange={set("details")} data-testid="contact-details" />
          <button type="submit" disabled={loading} data-testid="contact-submit"
            className="group sm:col-span-2 inline-flex items-center justify-center gap-2 px-7 py-4 bg-brand-coral text-brand-navy font-medium hover:bg-brand-sand transition-colors disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Get a quote <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
